package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.components.widgets.Figure
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 400.px

@InitRoute
fun initTypesOfQuartz(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

private val quartzKeyCharacteristics = listOf(
    "Hardness" to "7 on the Mohs scale — the benchmark the scale itself uses, and hard enough to scratch window glass and steel."
    , "Crystal system" to "Trigonal, usually growing as six-sided prisms capped by a pointed termination."
    , "Fracture" to "Conchoidal, breaking in smooth curved shells rather than along flat planes; quartz has no cleavage."
    , "Luster" to "Vitreous — glassy — on crystal faces, though the cryptocrystalline varieties look waxy instead."
    , "Streak" to "White, regardless of the color of the specimen."
    , "Composition" to "Silicon dioxide (SiO₂), the second most abundant mineral in the Earth's continental crust."
)

/**
 * Quartz grows two ways, and the split explains why one mineral wears so many names. Crystals big enough
 * to see are macrocrystalline; crystals too small to see are cryptocrystalline.
 */
private val macrocrystallineVarieties = listOf(
    "Rock Crystal" to "Colorless and transparent, the pure form with no trace elements to tint it."
    , "Amethyst" to "Purple, from iron impurities altered by natural radiation; heat turns much of it yellow."
    , "Citrine" to "Yellow to orange-brown. Genuinely natural citrine is uncommon, and most on the market is heat-treated amethyst."
    , "Smoky Quartz" to "Brown to near-black, colored by irradiated aluminum impurities rather than by any added element."
    , "Rose Quartz" to "Pink and usually cloudy, colored by microscopic mineral fibers; it rarely forms clean crystals."
    , "Milky Quartz" to "White and opaque from countless fluid inclusions trapped as the crystal grew. The most common variety of all."
    , "Rutilated Quartz" to "Clear quartz threaded with golden needles of rutile that grew before the quartz enclosed them."
    , "Aventurine" to "Green or blue-green with a glittery sheen from inclusions of mica or hematite."
)

/**
 * The cryptocrystalline side of the family. These each have their own page, since between them they
 * account for most of what a rockhound actually picks up.
 */
private val cryptocrystallineVarieties = listOf(
    Triple(
        "Chalcedony"
        , "The catch-all name for cryptocrystalline quartz — waxy rather than glassy, because the crystals are microscopic."
        , "/types_of_rocks/chalcedony"
    )
    , Triple(
        "Agate"
        , "Translucent chalcedony with banding, formed layer by layer in a cavity."
        , "/types_of_rocks/agate"
    )
    , Triple(
        "Jasper"
        , "Opaque chalcedony, carrying enough iron and clay to stop light entirely."
        , "/types_of_rocks/jasper"
    )
    , Triple(
        "Carnelian"
        , "Translucent chalcedony colored an even red-orange by iron oxide, with little or no banding."
        , "/types_of_rocks/carnelian"
    )
)

/** Outside references, paired with what each one is actually good for. */
private val furtherReading = listOf(
    Triple(
        "Wikipedia: Quartz"
        , "The general overview — structure, chemistry, varieties, and history, with sources worth following."
        , "https://en.wikipedia.org/wiki/Quartz"
    )
    , Triple(
        "Mindat: Quartz"
        , "Mineral data and, more useful to a collector, a locality database recording where quartz has actually been found."
        , "https://www.mindat.org/min-3337.html"
    )
    , Triple(
        "Geology.com: Quartz"
        , "Plain-language coverage of properties and uses, heavy on photographs of real specimens."
        , "https://geology.com/minerals/quartz.shtml"
    )
    , Triple(
        "Minerals.net: Quartz"
        , "A variety-by-variety breakdown, handy when you are trying to put a name to a particular color or habit."
        , "https://www.minerals.net/mineral/quartz.aspx"
    )
    , Triple(
        "Wikipedia: Amethyst"
        , "A closer look at the most collected variety, including why heat turns so much of it yellow."
        , "https://en.wikipedia.org/wiki/Amethyst"
    )
)

@Composable
private fun TheManyFacesOfQuartzHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "The Many Faces of Quartz"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfQuartzHero() {

    TheManyFacesOfQuartzHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/quartz/the_many_faces_of_quartz.png"
                    , alt = "the many varieties of quartz"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Quartz is silicon dioxide (SiO₂), and it is everywhere — the second most abundant mineral in the Earth's continental crust, a major ingredient of granite, the sand on most beaches, and the parent of agate, jasper, and carnelian. Hardness 7 and no cleavage make it tough enough to survive everything that destroys softer minerals, which is exactly why so much of what survives in a creek bed is quartz of one kind or another.")
            }

            SpanText(
                "How It Forms"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = Alignment.Start)
            )
            P {
                Text("Quartz crystallizes out of silica-rich fluids and melts across an enormous range of conditions. It grows late in cooling magma, once the other minerals have taken what they need, which is why granite is full of it. It precipitates from hot water circulating through fractures, filling them with veins and lining cavities with crystals. It fills gas bubbles in old lava slowly enough to band, which is how agate forms. Whether you end up with a clear crystal the length of your hand or a waxy nodule with no visible crystals at all comes down to how much room there was and how fast the silica came out of solution.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        SpanText(
            "Key Characteristics"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )

        Ul(PageListStyle.toAttrs()) {
            quartzKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        SpanText(
            "Types and Varieties"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Quartz varieties divide first by crystal size. Where there was room and time, it grows crystals you can see — the macrocrystalline varieties, named mostly for color:")
        }

        Ul(PageListStyle.toAttrs()) {
            macrocrystallineVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        P {
            Text("Where the crystals are too small to see, the result is cryptocrystalline quartz. These are the stones most rockhounds spend their time on, and each has its own page:")
        }

        Ul(PageListStyle.toAttrs()) {
            cryptocrystallineVarieties.forEach { (name, description, route) ->
                Li {
                    B { Link(route, name) }
                    Text(" — $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/quartz/quartz-rose.jpg"
            , alt = "rose quartz"
            , caption = "rose quartz"
            , credit = "Photo: Geology rocks & minerals"
        )

        SpanText(
            "Macrocrystalline vs. Cryptocrystalline"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("The two groups are the same mineral with the same hardness and the same chemistry, and the difference is only the size of the crystals. It shows up in the luster: macrocrystalline quartz looks glassy, because light reflects off flat crystal faces, while cryptocrystalline quartz looks waxy or greasy, because the surface is a felt of microscopic crystals scattering light in every direction. That waxy rind is the field test — it is how you spot an agate nodule in a gravel bar before you have cut it open.")
        }

        SpanText(
            "Historical and Cultural Uses"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Quartz was a tool before it was an ornament. Its conchoidal fracture lets a knapper take controlled flakes off a core, and quartz and its cryptocrystalline varieties were worked into blades and points wherever better flint was not available. Later it became ornament and symbol: rock crystal carved into seals and vessels, amethyst set into Egyptian and Roman jewelry and long held — on the strength of a Greek pun on the word for intoxication — to protect the wearer from drunkenness.")
        }

        SpanText(
            "Modern Uses"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Quartz is piezoelectric: squeeze a crystal and it generates a voltage, apply a voltage and it flexes. Cut a sliver to the right dimensions and it vibrates at a precise, stable frequency, which is the whole basis of the quartz watch and the clock in every computer. Those crystals are grown in an autoclave rather than mined. Beyond electronics, quartz sand is the raw material for glass and for silicon itself, and the decorative varieties keep the lapidary trade supplied with cabochons, beads, and tumbled stones.")
        }

        SpanText(
            "Finding It in the Field"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Quartz is common enough that the trick is not finding it but finding good material. White vein quartz turns up in almost any gravel; the crystals worth keeping come from cavities, so look where veins have weathered open or where a road cut has exposed a pocket. Amethyst and smoky quartz favor granite and volcanic country, and rose quartz comes mostly out of pegmatites in massive chunks rather than crystals. As always, check the land status before you fill a bucket — BLM ground, private land, and state parks each have their own rules.")
        }

        SpanText(
            "For More Information"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )

        Ul(PageListStyle.toAttrs()) {
            furtherReading.forEach { (title, description, url) ->
                Li {
                    B { Link(url, title) }
                    Text(" — $description")
                }
            }
        }
    }
}

@Composable
private fun TypesOfQuartz_BooksSection() {
    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on Quartz"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = booksOnQuartzCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("/types_of_rocks/quartz")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfQuartzPage() {
    TypesOfQuartzHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    TypesOfQuartz_BooksSection()
}

/**
 * Placeholder slide. The cover art, title, rating, and review are yours to fill in — they are left as
 * TODOs rather than invented, so nothing untrue ships with the page.
 */
val booksOnQuartzCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/typesof/quartz/book_quartz_1.webp"
                , alt = "Quartz, An introduction to..."
                , modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column {
                SpanText(
                    "QUARTZ, An Introduction to Crystalline Quartz: Decoding the messages from inside the earth."
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
//                SpanText(
//                    "TODO: rating"
//                    , LearnBookRatingStyle.toModifier()
//                        .color(textColor())
//                        .align(alignment = Alignment.Start)
//                )

                P {
                    Text("This book breaks new ground in several ways. The internal structures of quartz crystals are covered as well as its external forms. It explains the latest concepts so that they may be understood by novices and be of interest to professional mineralogists. It describes many forms of quartz crystals that have not been covered in previous books. The CD, which is included, clearly illustrates the internal atomic arrangements and growth mechanisms in a manner that the two-dimensional pages of a book cannot. The specimens used in the book were not selected merely for their beauty, but for their unique qualities. This internationally acclaimed book is enjoyable, entertaining and educational.")
                }
            }
        }
    }
)
